/* Firebase Cloud Messaging — registro del dispositivo del dueño para recibir
 * push de reservas nuevas aunque la pestaña esté cerrada.
 *
 * INERTE hasta definir REACT_APP_FCM_VAPID_KEY (Firebase console → Cloud
 * Messaging → Web Push certificates → Key pair). Sin esa clave no hace nada.
 *
 * Caveat: en iOS el push web SOLO funciona si el dueño instaló la PWA
 * (Agregar a inicio). En Android/desktop funciona con el navegador normal.
 *
 * SCOPING POR MENÚ: un mismo usuario puede tener varias cartas/restaurantes.
 * El token guarda `menus: [menuId, ...]` = los restaurantes de los que ESTE
 * dispositivo quiere avisos. El trigger filtra por ahí, así dos encargados
 * (cada uno de un restaurante, mismo login) no comparten notificaciones.
 *   - `menus` AUSENTE = token legacy → recibe TODO (retrocompatible).
 *   - `menus` PRESENTE = allowlist explícita (vacío = no recibe nada).
 */
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, arrayUnion, arrayRemove } from "firebase/firestore";
import { app, db } from "../../../firebase";
import { getServiceDate } from "../utils/bookingUtils";

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;

// Último token conocido de este dispositivo. NO cortocircuitamos getToken con
// esto: FCM rota el token y getToken() devuelve el vigente (cacheado en
// IndexedDB por el SDK), así que llamarlo siempre evita operar sobre un token
// muerto tras revocar→reconceder permiso o reinstalar la PWA. Lo guardamos solo
// para poder borrar el doc correcto en logout / al revocarse el permiso.
let lastToken = null;

/* Exportada: el admin (RA Admin) registra el MISMO token del dispositivo en su
 * propia colección (/admin_devices) para los recordatorios de tareas. */
export const PUSH_CONFIGURED = !!VAPID_KEY;

export async function getDeviceToken() {
  try {
    if (!VAPID_KEY) return null;
    if (!(await isSupported())) return null;
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    lastToken = token || null;
    return lastToken;
  } catch (e) {
    console.warn("getDeviceToken failed:", e);
    return null;
  }
}

/* Resetea el cache del token (logout / cambio de cuenta). */
export function resetTokenCache() {
  lastToken = null;
}

// "/" no es válido en IDs de Firestore → el id del doc es el token saneado.
const tokenDocRef = (uid, token) =>
  doc(db, "fcm_tokens", uid, "tokens", token.replace(/\//g, "_"));

/* Registra (o refresca) el token FCM de ESTE dispositivo y lo suscribe al menú
 * dado (si se pasa). Guarda en /fcm_tokens/{uid}/tokens/{tokenId} (owner-only).
 * Devuelve el token o null. Llamar tras conceder permiso (gesto del usuario). */
export async function registerFcmToken(uid, menuId) {
  try {
    if (!uid) return null;
    const token = await getDeviceToken();
    if (!token) return null;
    await setDoc(
      tokenDocRef(uid, token),
      {
        token,
        platform: typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
        updatedAt: serverTimestamp(),
        ...(menuId ? { menus: arrayUnion(menuId) } : {}),
      },
      { merge: true }
    );
    return token;
  } catch (e) {
    console.warn("registerFcmToken failed:", e);
    return null;
  }
}

/* Suscribe ESTE dispositivo a los avisos del menú (registra el token si hace
 * falta). Nombre explícito para el toggle. */
export async function subscribeMenu(uid, menuId) {
  return registerFcmToken(uid, menuId);
}

/* Desuscribe ESTE dispositivo del menú. Si el doc era legacy (sin `menus`),
 * arrayRemove materializa el campo explícito → pasa a allowlist. El toggle
 * avisa al dueño cuando esto pasa (deja de recibir el resto hasta re-activar). */
export async function unsubscribeMenu(uid, menuId) {
  try {
    if (!uid || !menuId) return;
    const token = await getDeviceToken();
    if (!token) return;
    await setDoc(
      tokenDocRef(uid, token),
      { menus: arrayRemove(menuId), updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (e) {
    console.warn("unsubscribeMenu failed:", e);
  }
}

/* Borra el token de ESTE dispositivo del usuario. Llamar en LOGOUT (antes de
 * signOut, mientras las rules todavía permiten el delete): si no, el token queda
 * bajo la cuenta vieja y el próximo que use el celular recibe sus reservas, y el
 * doc ya no se puede borrar nunca (regla owner-only). */
export async function unregisterDevice(uid) {
  try {
    if (!uid) return;
    let token = lastToken;
    if (!token) {
      // Solo buscamos el token si el permiso está concedido: si no, no hay doc
      // que borrar y evitamos registrar el SW en el logout de quien nunca activó.
      const granted =
        typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted";
      if (granted) token = await getDeviceToken();
    }
    if (token) await deleteDoc(tokenDocRef(uid, token));
  } catch (e) {
    console.warn("unregisterDevice failed:", e);
  } finally {
    resetTokenCache();
  }
}

/* Estado de notificaciones de ESTE dispositivo para un menú:
 *   { supported, permission, registered, subscribed, legacy }
 *   - supported: el browser soporta push + hay VAPID key.
 *   - permission: "granted" | "denied" | "default" | "unsupported".
 *   - registered: el dispositivo tiene token guardado (si no, no recibe nada).
 *   - subscribed: recibe avisos de ESTE menú (legacy sin `menus` = true).
 *   - legacy: el token recibe TODO (sin allowlist) → el toggle avisa al apagar. */
export async function getNotifState(uid, menuId) {
  const hasNotif = typeof window !== "undefined" && "Notification" in window;
  const permission = hasNotif ? Notification.permission : "unsupported";
  let supported = hasNotif && !!VAPID_KEY;
  if (supported) {
    try { supported = await isSupported(); } catch { supported = false; }
  }
  const base = { supported, permission, registered: false, subscribed: false, legacy: false };
  if (!supported || permission !== "granted" || !uid) {
    // Permiso REVOCADO con un token que teníamos en memoria → borramos el doc
    // fantasma para que el trigger no lo siga targeteando (sends desperdiciados).
    if (permission === "denied" && lastToken && uid) {
      deleteDoc(tokenDocRef(uid, lastToken)).catch(() => {});
      resetTokenCache();
    }
    return base;
  }
  try {
    const token = await getDeviceToken();
    if (!token) return base;
    const snap = await getDoc(tokenDocRef(uid, token));
    if (!snap.exists()) return base;
    const menus = snap.data().menus;
    const legacy = menus === undefined; // recibe todo
    const subscribed = legacy ? true : Array.isArray(menus) && menus.includes(menuId);
    return { supported, permission, registered: true, subscribed, legacy };
  } catch (e) {
    console.warn("getNotifState failed:", e);
    return base;
  }
}

/* Handler FOREGROUND: cuando la PWA está ABIERTA y enfocada, FCM entrega el push
 * a este callback (NO al service worker). Sin esto, una reserva en OTRA carta no
 * avisa con la app abierta — justo el caso multi-carta que la feature resuelve.
 * Mostramos la notificación del sistema, salvo que ya estés viendo la pantalla
 * de reservas de ESE menú (ahí el beep in-tab ya avisa → no duplicamos).
 * Idempotente-ish: registrar una vez al cargar la app. Inerte sin VAPID/soporte. */
let foregroundInited = false;
export function initForegroundMessages() {
  if (foregroundInited || !VAPID_KEY || typeof window === "undefined") return;
  foregroundInited = true;
  isSupported()
    .then((ok) => {
      if (!ok) return;
      try {
        const messaging = getMessaging(app);
        onMessage(messaging, async (payload) => {
          const d = payload.data || {};
          try {
            // url = /profile/{uid}/{menuId}/reservas?pend=1 → menuId en índice 3
            const menuId = (d.url || "").split("/")[3];
            if (menuId) {
              const seg = "/" + menuId + "/reservas";
              const path = window.location.pathname;
              // Solo Hoy (index), /dia/:date y /mes montan el beep in-tab. En
              // config/stats/mapa/clientes NO hay beep → NO suprimimos el push.
              const onAlertScreen =
                path.endsWith(seg) || path.includes(seg + "/dia/") || path.includes(seg + "/mes");
              // El beep in-tab solo cubre reservas de HOY. Si la reserva es de otra
              // fecha (hasta 60 días adelante), mostramos la notif del sistema igual.
              // `d.date` ausente (push viejo) → tratamos como hoy (comportamiento previo).
              const isTodayService = !d.date || d.date === getServiceDate(new Date());
              if (onAlertScreen && isTodayService) return;
            }
          } catch (e) {
            /* si no podemos parsear, mostramos igual */
          }
          try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) {
              reg.showNotification(d.title || "Nueva reserva", {
                body: d.body || "",
                tag: d.tag || "reserva-nueva",
                data: d,
                requireInteraction: true,
              });
            }
          } catch (e) {
            /* no-op */
          }
        });
      } catch (e) {
        console.warn("initForegroundMessages failed:", e);
      }
    })
    .catch(() => {});
}
